import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import profileData from '../data/profileData.json';

const Contact = () => {
  const { personal } = profileData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast({
          title: "Message Sent!",
          description: "Thank you for your message. I'll get back to you soon.",
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });

    // try {
    //   const response = await fetch('http://localhost:8000/api/contact', {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json',
    //     },
    //     body: JSON.stringify(formData),
    //   });

    //   if (response.ok) {
    //     toast({
    //       title: "Message Sent!",
    //       description: "Thank you for your message. I'll get back to you soon.",
    //     });
    //     // Reset form
    //     setFormData({
    //       name: '',
    //       email: '',
    //       subject: '',
    //       message: ''
    //     });
    //   } else {
    //     throw new Error('Failed to send message');
    //   }
    // } catch (error) {
    //   console.error('Error sending message:', error);
    //   toast({
    //     title: "Error",
    //     description: "Failed to send message. Please try again later.",
    //     variant: "destructive",
    //   });
    // }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-[#1e3a5f] mb-4">Get In Touch</h1>
          <div className="h-1 w-20 bg-[#2563eb]"></div>
          <p className="mt-6 text-lg text-gray-600">
            I'm always open to discussing new projects, opportunities, or collaborations.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Mail size={24} className="text-[#2563eb]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Email</h3>
                    <a href={`mailto:${personal.email}`} className="text-gray-600 hover:text-[#2563eb] text-sm">
                      {personal.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Phone size={24} className="text-[#2563eb]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Phone</h3>
                    <a href={`tel:${personal.phone}`} className="text-gray-600 hover:text-[#2563eb] text-sm">
                      {personal.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <MessageCircle size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">WhatsApp</h3>
                    <a
                      href={`https://wa.me/${personal.social.whatsapp.replace('+', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-green-600 text-sm"
                    >
                      Message me on WhatsApp
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Send size={24} className="text-blue-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#1e3a5f] mb-1">Telegram</h3>
                    <a
                      href={`https://t.me/${personal.social.telegram}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 hover:text-blue-500 text-sm"
                    >
                      Message me on Telegram
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-[#1e3a5f]">Send Me a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name
                      </label>
                      <Input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Doe"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Email
                      </label>
                      <Input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject
                    </label>
                    <Input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      placeholder="Project Opportunity"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      placeholder="Tell me about your project..."
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="bg-[#2563eb] hover:bg-[#1e40af] text-white px-8 py-3 w-full md:w-auto"
                  >
                    Send Message
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;